/**
 * WHAT AN INSTRUCTION SAYS ABOUT WHO AND WHEN (ADR-0021 §9 part 2) — read from
 * the person's own words, so the chat box asks nothing it can read.
 *
 * Deterministic and shared: the server resolves the work on this reading, and
 * the chat box shows the same reading as the person types. Words are read,
 * never guessed at: a period is only found when the words name one, and a
 * client only when its name is in them.
 */
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const SHORT = MONTHS.map((m) => m.slice(0, 3));
const TITLE = (m) => MONTHS[m][0].toUpperCase() + MONTHS[m].slice(1);
const pad = (n) => String(n).padStart(2, "0");
const lastDay = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const monthPeriod = (y, m) => ({ from: iso(y, m, 1), to: iso(y, m, lastDay(y, m)), label: `${TITLE(m)} ${y}`, span: "MONTH" });
const quarterPeriod = (y, q) => ({ from: iso(y, (q - 1) * 3, 1), to: iso(y, q * 3 - 1, lastDay(y, q * 3 - 1)), label: `Q${q} ${y}`, span: "QUARTER" });
const yearEnded = (y, m, d) => {
    const endMonth = m;
    const startY = endMonth === 11 ? y : y - 1;
    const startM = (endMonth + 1) % 12;
    return { from: iso(startY, startM, 1), to: iso(y, m, d), label: `Year ended ${d} ${TITLE(m)} ${y}`, span: "YEAR" };
};
const monthIndex = (word) => {
    const w = word.toLowerCase();
    const full = MONTHS.indexOf(w);
    if (full >= 0)
        return full;
    return w.length === 3 || (w.length === 4 && w.endsWith(".")) ? SHORT.indexOf(w.slice(0, 3)) : -1;
};
const fullYear = (y) => (y.length === 2 ? 2000 + Number(y) : Number(y));
/** The period an instruction names, read against today's date ("YYYY-MM-DD"); null when it names none. */
export function periodFromWords(instruction, today) {
    const text = ` ${String(instruction ?? "").replace(/\s+/g, " ").trim()} `;
    const [ty, tm] = [Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1];
    // "year ended 31 March 2026", "year ending 31 December 2025"
    const ye = /\byear\s+end(?:ed|ing)\s+(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})\b/i.exec(text);
    if (ye) {
        const m = monthIndex(ye[2]);
        if (m >= 0)
            return yearEnded(Number(ye[3]), m, Math.min(Number(ye[1]), lastDay(Number(ye[3]), m)));
    }
    // "Q1 2026", "first quarter 2026"
    const q = /\bq([1-4])\s*(?:of\s+)?(\d{4}|'?\d{2})?\b/i.exec(text) ?? /\b(first|second|third|fourth)\s+quarter(?:\s+of)?\s*(\d{4})?\b/i.exec(text);
    if (q) {
        const n = /^\d$/.test(q[1]) ? Number(q[1]) : ["first", "second", "third", "fourth"].indexOf(q[1].toLowerCase()) + 1;
        const y = q[2] ? fullYear(q[2].replace("'", "")) : (n - 1) * 3 > tm ? ty - 1 : ty;
        return quarterPeriod(y, n);
    }
    // "March 2026", "Mar 2026", "march" (the most recent March), "for May" — but "may" alone is only a month with a year or a preposition
    const month = /\b(?:(for|in|of|during)\s+)?(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\.?(?:\s+(\d{4}|'\d{2}))?\b/i.exec(text);
    if (month) {
        const word = month[2].toLowerCase();
        const m = word === "sept" ? 8 : monthIndex(word);
        const isShortOrMay = word.length <= 4 || word === "may";
        if (m >= 0 && (!isShortOrMay || month[3] || month[1])) {
            const y = month[3] ? fullYear(month[3].replace("'", "")) : m > tm ? ty - 1 : ty;
            return monthPeriod(y, m);
        }
    }
    if (/\blast month\b/i.test(text))
        return monthPeriod(tm === 0 ? ty - 1 : ty, (tm + 11) % 12);
    if (/\bthis month\b/i.test(text))
        return monthPeriod(ty, tm);
    const cq = Math.floor(tm / 3) + 1;
    if (/\blast quarter\b/i.test(text))
        return cq === 1 ? quarterPeriod(ty - 1, 4) : quarterPeriod(ty, cq - 1);
    if (/\bthis quarter\b/i.test(text))
        return quarterPeriod(ty, cq);
    if (/\blast year\b/i.test(text))
        return yearEnded(ty - 1, 11, 31);
    // "2025 accounts", "FY2025", "for 2025" — a calendar year
    const y = /\b(?:fy\s*)?(20\d{2})\b/i.exec(text);
    if (y)
        return yearEnded(Number(y[1]), 11, 31);
    return null;
}
const LEGAL = new Set(["ltd", "limited", "inc", "llc", "llp", "plc", "co", "corp", "company", "group", "the", "and", "&", "sa", "gmbh", "bv", "pty"]);
const tokens = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9&\s]/g, " ").split(/\s+/).filter(Boolean);
const significant = (name) => tokens(name).filter((t) => !LEGAL.has(t) && t.length > 1);
/**
 * Which of these clients the instruction names. People say a client's short
 * name ("Northwind" for Northwind Trading Ltd): a client is named when the
 * first significant word of its name is said, and the more of its words are
 * said, the more surely — a name said in full is surer still. The surest wins;
 * equally sure names are all returned, and the person chooses.
 */
export function clientsNamedIn(instruction, clients) {
    const said = new Set(tokens(instruction));
    const scored = clients
        .map((c) => ({ c, words: significant(c.name) }))
        .filter((x) => x.words.length > 0 && said.has(x.words[0]))
        .map((x) => ({ c: x.c, score: x.words.filter((w) => said.has(w)).length + (x.words.every((w) => said.has(w)) ? 0.5 : 0) }));
    if (scored.length === 0)
        return [];
    const best = Math.max(...scored.map((x) => x.score));
    return scored.filter((x) => x.score === best).map((x) => x.c);
}
/** Words that belong to accounting work at all; an instruction with none of them (and no client) is not accounting. */
const ACCOUNTING_TALK = /\b(account|accounts|accounting|ledger|balance|balances|invoice|invoices|journal|tax|vat|gst|asset|assets|expense|expenses|revenue|income|payable|payables|receivable|receivables|supplier|suppliers|vendor|vendors|customer|customers|client|clients|books|bookkeeping|close|closing|audit|statement|statements|trial|payment|payments|receipt|receipts|reconcil\w*|depreciat\w*|payroll|cash|bank|stock|inventory|creditor|creditors|debtor|debtors|record|records|transaction|transactions|entry|entries|accrual|accruals|prepayment|prepayments|fee|fees|charge|credit|debit|month-end|year-end|period|figures?|categori[sz]\w*|classif\w*|work|outstanding|waiting|prepar\w*|draft|p&l|profit|loss)\b/i;
// "Do", "does", "did", "is", "has"… open a question only before who it asks about ("Do we have…", "Is Acme…?");
// "Do the prepayments for Acme" is an instruction.
const ASKS = /^\s*(what|whats|what's|why|how|which|who|when|where|show me|list|tell me)\b|^\s*(is|are|did|does|do|has|have|was|were)\s+(i|we|you|they|there|it|anything|any|anyone|my|our|the client)\b|\?\s*$/i;
/**
 * Leveri's own reading of WHAT KIND of thing an instruction is, when no model
 * reads it: a question about the firm's work, accounting work to do, or
 * something Leveri does not do at all. The model, when configured, decides
 * this better; these words are the floor.
 */
const ABOUT_THE_WORK = /\b(work|works|outstanding|waiting|pending|open|done|finished|prepared|progress|clients?|engagements?|requests?|asked|chase|status|left|to ?do|assigned|mine|my plate|yesterday|today|this week|last week)\b/i;
/**
 * `inContext`: the words were said where the work is (a client's or an
 * engagement's page), or name a client or a period — they are about the
 * firm's work even without an accounting word ("Sort out March").
 */
export function intentFromWords(instruction, inContext, files = 0) {
    const text = String(instruction ?? "").trim();
    if (!text)
        return "WORK"; // files alone are work
    const asks = ASKS.test(text) && !/^\s*(can|could|would|will)\s+you\b/i.test(text);
    // A question is about the firm's work, or it is not Leveri's to answer (general knowledge, even accounting knowledge).
    if (asks && files === 0)
        return ABOUT_THE_WORK.test(text) || inContext ? "QUESTION" : "OUT_OF_SCOPE";
    if (files > 0)
        return "WORK";
    return ACCOUNTING_TALK.test(text) || inContext ? "WORK" : "OUT_OF_SCOPE";
}
/** What a question about the firm's work asks for, read from its words; "ALL" when they say no more. */
export function questionFocusFromWords(instruction) {
    const text = String(instruction ?? "");
    if (/\b(done|finished|completed?|prepared|ready|signed)\b/i.test(text))
        return "DONE";
    if (/\b(in progress|working on|running|underway)\b/i.test(text))
        return "IN_PROGRESS";
    if (/\b(outstanding|waiting|pending|open|owed|asked|chase|stuck|attention|left|to do|todo|on my plate|blocked)\b/i.test(text))
        return "OUTSTANDING";
    return "ALL";
}
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * A span of real dates, named in Leveri's own words ("March 2026", "Q1 2026",
 * "Year ended 31 March 2026", or "1 April 2025 to 31 March 2026"): a period
 * another reader proposed is shown in these words, never in its own.
 */
export function periodBetween(from, to) {
    const a = DAY.exec(from);
    const b = DAY.exec(to);
    if (!a || !b)
        return null;
    const [fy, fm, fd] = [Number(a[1]), Number(a[2]) - 1, Number(a[3])];
    const [ty, tm, td] = [Number(b[1]), Number(b[2]) - 1, Number(b[3])];
    if (iso(fy, fm, fd) !== from || iso(ty, tm, td) !== to || fm > 11 || tm > 11 || fd > lastDay(fy, fm) || td > lastDay(ty, tm) || from > to)
        return null;
    const months = (ty - fy) * 12 + (tm - fm) + 1;
    const wholeMonths = fd === 1 && td === lastDay(ty, tm);
    if (wholeMonths && months === 1)
        return monthPeriod(fy, fm);
    if (wholeMonths && months === 3 && fy === ty && fm % 3 === 0)
        return quarterPeriod(fy, fm / 3 + 1);
    if (wholeMonths && months === 12)
        return { from, to, label: `Year ended ${td} ${TITLE(tm)} ${ty}`, span: "YEAR" };
    const days = (Date.UTC(ty, tm, td) - Date.UTC(fy, fm, fd)) / 86_400_000;
    return { from, to, label: `${fd} ${TITLE(fm)} ${fy} to ${td} ${TITLE(tm)} ${ty}`, span: days <= 31 ? "MONTH" : days <= 92 ? "QUARTER" : "YEAR" };
}
/**
 * Accounting work Leveri has no skill for yet, named in the words — so the
 * chat says so plainly even when no model reads them. Returns the person's
 * own word for it, or null. Read only when no skill was named.
 */
const NOT_YET = [
    [/\bprepayments?\b/i, "prepayments"],
    [/\baccruals? (schedule|journal)s?\b/i, "an accruals schedule"],
    [/\bpayroll\b|\bpayslips?\b/i, "payroll"],
    [/\b(tax|vat|gst|sales tax) returns?\b/i, "tax returns"],
    [/\bfixed asset register\b|\bdepreciation schedule\b/i, "a fixed asset register"],
    [/\bbudgets?\b|\bforecasts?\b/i, "budgets and forecasts"],
    [/\b(raise|send|issue) (an? )?invoices?\b/i, "invoicing"],
];
export function unsupportedWorkFromWords(instruction) {
    const text = String(instruction ?? "");
    for (const [words, name] of NOT_YET)
        if (words.test(text))
            return name;
    return null;
}
//# sourceMappingURL=chatWords.js.map